"use client";

import { useState, useEffect, useRef } from 'react';
import useUserStore from '@/store/userStore';

/**
 * Enhanced debug widget to display auth status and API activity
 */
const AuthStatusWidget = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    tokenStatus: 'Checking...',
    tokenAge: null,
    lastRefreshAttempt: null,
    tokenExpiryTime: null,
    timeToExpiry: null,
    backendVerificationStatus: null,
    tokenDetails: null,
    errors: []
  });
  
  // Improved API call tracking with categories
  const [apiCalls, setApiCalls] = useState({
    total: 0,
    verification: 0,  // check_user_created endpoint
    profile: 0,       // user_profile endpoint
    other: 0          // all other backend endpoints
  });
  
  // Track recent API calls for display
  const [recentCalls, setRecentCalls] = useState([]);
  const maxRecentCalls = 5;  // Keep only the most recent 5 calls
  
  // Reference to original fetch
  const originalFetchRef = useRef(null);
  
  const { 
    user, 
    isLoggedIn, 
    loading, 
    error, 
    verifiedWithBackend,
    checkCurrentAuth,
    accessToken,
    refreshTokenIfNeeded,
    fetchWithTokenRefresh,
    getFirebaseToken,
    profileData
  } = useUserStore();

  // Function to decode JWT token and extract all details
  const decodeJwt = (token) => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error("Error decoding token:", e);
      return null;
    }
  };

  // Get more details for debugging
  const handleCheckAuth = () => {
    const authDetails = checkCurrentAuth();
    console.log('Current auth state:', authDetails);
    alert(JSON.stringify(authDetails, null, 2));
  };
  
  // Function to check token status with detailed information
  const checkTokenStatus = async () => {
    const newDebugInfo = { ...debugInfo, errors: [] };
    
    try {
      if (!isLoggedIn) {
        newDebugInfo.tokenStatus = 'Not logged in';
        setDebugInfo(newDebugInfo);
        return;
      }

      // Check if we have a token
      if (!accessToken) {
        newDebugInfo.tokenStatus = 'No token available';
        setDebugInfo(newDebugInfo);
        return;
      }
      
      // Decode token to get all details
      const decodedToken = decodeJwt(accessToken);
      
      if (!decodedToken) {
        newDebugInfo.tokenStatus = 'Invalid token format';
        setDebugInfo(newDebugInfo);
        return;
      }
      
      // Store complete token details
      newDebugInfo.tokenDetails = decodedToken;
      
      const expiryTime = decodedToken.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeToExpiry = expiryTime - currentTime;
      const tokenAgeMs = currentTime - (decodedToken.iat * 1000);
      
      newDebugInfo.tokenExpiryTime = new Date(expiryTime).toLocaleString();
      newDebugInfo.tokenAge = Math.floor(tokenAgeMs / 1000 / 60) + ' minutes';
      newDebugInfo.timeToExpiry = timeToExpiry > 0 ? 
        Math.floor(timeToExpiry / 1000 / 60) + ' minutes' : 
        'EXPIRED';
      
      if (timeToExpiry <= 0) {
        newDebugInfo.tokenStatus = 'EXPIRED';
      } else if (timeToExpiry < 5 * 60 * 1000) { // Less than 5 minutes
        newDebugInfo.tokenStatus = 'EXPIRING SOON';
      } else {
        newDebugInfo.tokenStatus = 'VALID';
      }

      // Only try to refresh if token is close to expiring
      if (timeToExpiry < 10 * 60 * 1000) {
        newDebugInfo.lastRefreshAttempt = new Date().toLocaleString();
        const freshToken = await refreshTokenIfNeeded(true);
        
        if (freshToken !== accessToken) {
          newDebugInfo.tokenStatus = 'REFRESHED';
          // Re-decode the new token
          const newDecodedToken = decodeJwt(freshToken);
          if (newDecodedToken) {
            newDebugInfo.tokenDetails = newDecodedToken;
            const newExpiryTime = newDecodedToken.exp * 1000;
            newDebugInfo.tokenExpiryTime = new Date(newExpiryTime).toLocaleString();
            const newTokenAgeMs = currentTime - (newDecodedToken.iat * 1000);
            newDebugInfo.tokenAge = Math.floor(newTokenAgeMs / 1000 / 60) + ' minutes';
            newDebugInfo.timeToExpiry = Math.floor((newExpiryTime - currentTime) / 1000 / 60) + ' minutes';
          }
        }
      }
      
      // Check backend verification status from state only
      newDebugInfo.backendVerificationStatus = verifiedWithBackend ? 'VERIFIED' : 'NOT VERIFIED';
      
      // Get session verification status
      try {
        newDebugInfo.sessionVerification = sessionStorage.getItem('backend_verified') === 'true' ? 'VERIFIED' : 'NOT VERIFIED';
      } catch (e) {
        newDebugInfo.sessionVerification = 'Error reading session storage';
      }
      
      // Set timestamp for the check
      newDebugInfo.lastChecked = new Date().toLocaleString();
      
    } catch (e) {
      newDebugInfo.errors.push(`Error checking token: ${e.message}`);
    }
    
    setDebugInfo(newDebugInfo);
  };
  
  // Enhanced API call tracking
  useEffect(() => {
    if (!originalFetchRef.current) {
      originalFetchRef.current = window.fetch;
    }
    
    // Patch fetch to monitor all API calls
    window.fetch = function(url, options) {
      // Only track backend API calls
      if (typeof url === 'string' && url.includes(process.env.NEXT_PUBLIC_BACKEND_URL)) {
        const timestamp = new Date().toLocaleTimeString();
        const endpoint = url.split('/').pop(); // Get the last part of the URL as endpoint name
        
        // Count specific endpoints and total
        setApiCalls(prev => {
          const newState = { ...prev, total: prev.total + 1 };
          
          if (url.includes('/check_user_created')) {
            newState.verification = prev.verification + 1;
          } else if (url.includes('/user_profile')) {
            newState.profile = prev.profile + 1;
          } else {
            newState.other = prev.other + 1;
          }
          
          return newState;
        });
        
        // Track recent calls for display
        setRecentCalls(prev => {
          const newCall = {
            timestamp,
            endpoint,
            url: url.replace(process.env.NEXT_PUBLIC_BACKEND_URL, ''),
            method: options?.method || 'GET'
          };
          
          // Keep only the most recent calls
          const updated = [newCall, ...prev].slice(0, maxRecentCalls);
          return updated;
        });
      }
      
      // Call original fetch with all arguments
      return originalFetchRef.current.apply(this, arguments);
    };
    
    // Cleanup
    return () => {
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
      }
    };
  }, []);

  // Run token check on component mount and periodically
  useEffect(() => {
    // Initial check
    checkTokenStatus();
    
    // Set up periodic checks with a reasonable interval
    const intervalId = setInterval(checkTokenStatus, 120000); // Every 2 minutes
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isLoggedIn, accessToken]);

  // Function to force token refresh
  const handleForceRefresh = async () => {
    try {
      const newToken = await getFirebaseToken(true);
      setDebugInfo(prev => ({
        ...prev,
        lastRefreshAttempt: new Date().toLocaleString(),
        tokenStatus: newToken ? 'MANUALLY REFRESHED' : 'REFRESH FAILED'
      }));
      
      // Re-check token status after forcing refresh
      setTimeout(checkTokenStatus, 1000);
    } catch (e) {
      setDebugInfo(prev => ({
        ...prev,
        errors: [...prev.errors, `Force refresh error: ${e.message}`]
      }));
    }
  };

  // Test user_profile endpoint
  const testBackendConnection = async () => {
    try {
      setDebugInfo(prev => ({ 
        ...prev, 
        backendConnectionStatus: 'Testing backend connection...' 
      }));
      
      // Use user_profile endpoint which should be available for logged-in users
      const profileUrl = `${process.env.NEXT_PUBLIC_BACKEND_URL}/user_profile`;
      const response = await fetchWithTokenRefresh(profileUrl);
      
      if (response.ok) {
        setDebugInfo(prev => ({
          ...prev,
          backendConnectionStatus: 'Connected successfully',
          backendResponse: 'Profile fetch successful'
        }));
      } else {
        setDebugInfo(prev => ({
          ...prev,
          backendConnectionStatus: `Failed with status: ${response.status}`,
          errors: [...prev.errors, `Backend request failed: ${response.status}`]
        }));
      }
    } catch (error) {
      setDebugInfo(prev => ({
        ...prev,
        backendConnectionStatus: 'Connection error',
        errors: [...prev.errors, `Backend connection error: ${error.message}`]
      }));
    }
  };

  // Reset API call counters
  const resetApiCallCounter = () => {
    setApiCalls({
      total: 0,
      verification: 0,
      profile: 0,
      other: 0
    });
    setRecentCalls([]);
  };

  // Collapsed mini widget
  if (!isExpanded) {
    return (
      <div className="fixed bottom-2 right-2 z-50">
        <button 
          onClick={() => setIsExpanded(true)}
          className="bg-gray-800 text-white px-3 py-1 rounded-md text-sm shadow-lg flex items-center space-x-1 hover:bg-gray-700"
        >
          <div className="flex items-center">
            <span>Auth Debug</span>
            {apiCalls.total > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                {apiCalls.total}
              </span>
            )}
          </div>
          {debugInfo.tokenStatus && (
            <div className={`ml-2 w-2 h-2 rounded-full ${
              debugInfo.tokenStatus === 'VALID' ? 'bg-green-400' : 
              debugInfo.tokenStatus === 'EXPIRED' ? 'bg-red-400' :
              debugInfo.tokenStatus === 'EXPIRING SOON' ? 'bg-yellow-400' :
              debugInfo.tokenStatus === 'REFRESHED' ? 'bg-blue-400' :
              'bg-gray-400'
            }`}></div>
          )}
        </button>
      </div>
    );
  }

  // Expanded widget
  return (
    <div className="fixed bottom-2 right-2 z-50 bg-white border border-gray-300 rounded-md shadow-lg p-4 w-96 text-sm overflow-auto max-h-[90vh]">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold">Auth Status</h3>
        <button 
          onClick={() => setIsExpanded(false)}
          className="text-gray-600 hover:text-gray-900"
        >
          ✕
        </button>
      </div>
      
      <div className="space-y-3">
        {/* Basic Auth Status */}
        <div className="grid grid-cols-2 gap-1">
          <div>
            <span className="font-medium">Login Status:</span> 
            <span className={`ml-1 ${isLoggedIn ? 'text-green-600' : 'text-red-600'}`}>
              {isLoggedIn ? 'Logged In' : 'Logged Out'}
            </span>
          </div>
          
          <div>
            <span className="font-medium">Loading:</span>
            <span className="ml-1">{loading ? 'Yes' : 'No'}</span>
          </div>
          
          <div>
            <span className="font-medium">Backend Verified:</span>
            <span className="ml-1">{verifiedWithBackend ? 'Yes' : 'No'}</span>
          </div>
          
          <div>
            <span className="font-medium">Session Storage:</span>
            <span className="ml-1">{debugInfo.sessionVerification || 'Unknown'}</span>
          </div>
        </div>
        
        {/* API Call Statistics */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium">API Activity</h4>
            <button 
              onClick={resetApiCallCounter}
              className="text-xs text-blue-600 hover:underline"
            >
              Reset Counters
            </button>
          </div>
          
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="bg-blue-50 p-2 rounded">
              <div className="text-lg font-bold text-blue-700">{apiCalls.total}</div>
              <div className="text-xs text-blue-600">Total</div>
            </div>
            <div className="bg-green-50 p-2 rounded">
              <div className="text-lg font-bold text-green-700">{apiCalls.profile}</div>
              <div className="text-xs text-green-600">Profile</div>
            </div>
            <div className="bg-amber-50 p-2 rounded">
              <div className="text-lg font-bold text-amber-700">{apiCalls.verification}</div>
              <div className="text-xs text-amber-600">Verify</div>
            </div>
            <div className="bg-purple-50 p-2 rounded">
              <div className="text-lg font-bold text-purple-700">{apiCalls.other}</div>
              <div className="text-xs text-purple-600">Other</div>
            </div>
          </div>
          
          {/* Recent API Calls */}
          {recentCalls.length > 0 && (
            <div className="mt-2">
              <h5 className="text-xs font-medium mb-1">Recent Calls:</h5>
              <div className="bg-gray-50 rounded p-2 max-h-24 overflow-auto text-xs">
                <table className="w-full">
                  <tbody>
                    {recentCalls.map((call, index) => (
                      <tr key={index} className="hover:bg-gray-100">
                        <td className="pr-2 text-gray-500">{call.timestamp}</td>
                        <td className="pr-2 text-gray-700">{call.method}</td>
                        <td className="text-blue-600 truncate">{call.url}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        
        {/* Token Status */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <h4 className="font-medium mb-2">Token Status</h4>
          <div className="flex items-center justify-between mb-2">
            <div className={`p-1 px-2 rounded text-xs font-medium ${
              debugInfo.tokenStatus === 'VALID' ? 'bg-green-100 text-green-800' : 
              debugInfo.tokenStatus === 'EXPIRED' ? 'bg-red-100 text-red-800' :
              debugInfo.tokenStatus === 'EXPIRING SOON' ? 'bg-yellow-100 text-yellow-800' :
              debugInfo.tokenStatus === 'REFRESHED' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {debugInfo.tokenStatus}
            </div>
            {debugInfo.timeToExpiry && (
              <div className="text-xs text-gray-600">
                {debugInfo.timeToExpiry === 'EXPIRED' 
                  ? 'Token has expired' 
                  : `Expires in: ${debugInfo.timeToExpiry}`}
              </div>
            )}
            {debugInfo.lastChecked && (
              <div className="text-xs text-gray-500">
                {new Date(debugInfo.lastChecked).toLocaleTimeString()}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-1 text-xs">
            <div><span className="font-medium">Token Age:</span> {debugInfo.tokenAge || 'N/A'}</div>
            <div><span className="font-medium">Last Refresh:</span> {debugInfo.lastRefreshAttempt ? new Date(debugInfo.lastRefreshAttempt).toLocaleTimeString() : 'None'}</div>
          </div>
          
          {/* Token Payload (Collapsible) */}
          {debugInfo.tokenDetails && (
            <div className="mt-2">
              <details className="text-xs">
                <summary className="cursor-pointer font-medium hover:text-blue-600">
                  Token Claims
                </summary>
                <div className="mt-1 p-2 bg-gray-100 rounded overflow-auto max-h-32">
                  <code className="whitespace-pre-wrap">
                    {JSON.stringify(debugInfo.tokenDetails, null, 2)}
                  </code>
                </div>
              </details>
            </div>
          )}
        </div>
        
        {/* User Data */}
        <div className="mt-2 pt-2 border-t border-gray-200">
          <h4 className="font-medium mb-1">User</h4>
          <div className="text-xs">
            <div><span className="font-medium">Email:</span> {user?.email || 'None'}</div>
            <div><span className="font-medium">UID:</span> {user?.uid || 'None'}</div>
            <div><span className="font-medium">Display Name:</span> {user?.displayName || 'None'}</div>
            {user?.photoURL && (
              <div className="mt-1 flex items-center">
                <span className="font-medium mr-2">Avatar:</span>
                <img src={user.photoURL} alt="User avatar" className="w-8 h-8 rounded-full" />
              </div>
            )}
          </div>
          
          {/* Profile Data (Collapsible) */}
          {profileData && (
            <div className="mt-1">
              <details className="text-xs">
                <summary className="cursor-pointer font-medium hover:text-blue-600">
                  Profile Data
                </summary>
                <div className="mt-1 p-2 bg-gray-100 rounded overflow-auto max-h-32">
                  <code className="whitespace-pre-wrap">
                    {JSON.stringify(profileData, null, 2)}
                  </code>
                </div>
              </details>
            </div>
          )}
        </div>
        
        {/* Errors */}
        {error && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <span className="text-red-600 font-medium">Auth Error:</span>
            <p className="text-red-600 text-xs mt-1 break-words">{error}</p>
          </div>
        )}
        
        {debugInfo.errors.length > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <h4 className="font-medium text-red-600">Errors</h4>
            <div className="bg-red-50 p-2 rounded text-xs mt-1">
              <ul className="list-disc list-inside">
                {debugInfo.errors.map((error, i) => (
                  <li key={i} className="text-red-700">{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap gap-2">
          <button
            onClick={checkTokenStatus}
            className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
          >
            Refresh Status
          </button>
          <button
            onClick={handleForceRefresh}
            className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
          >
            Force Token Refresh
          </button>
          <button
            onClick={testBackendConnection}
            className="px-2 py-1 bg-purple-500 text-white rounded text-xs hover:bg-purple-600"
          >
            Test Backend
          </button>
          <button
            onClick={handleCheckAuth}
            className="px-2 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-800"
          >
            Check Auth Details
          </button>
        </div>
      </div>
    </div>
  );
};
 
export default AuthStatusWidget;